import React, { useEffect, useState } from "react";
import { Plus, ShieldAlert, MessageSquare } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, fmtDate, EmptyState } from "../components/ui";

const CATS = ["HARASSMENT", "DISCRIMINATION", "WORKPLACE", "COMPENSATION", "MANAGER", "OTHER"];
const STATUS_TONE = { OPEN: "warn", IN_REVIEW: "info", RESOLVED: "ok", CLOSED: "neutral" };

export default function Grievances() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [comment, setComment] = useState("");
  const [resolution, setResolution] = useState("");
  const [form, setForm] = useState({ category: "WORKPLACE", subject: "", description: "", confidential: false });
  const isHR = ["SUPERADMIN", "HR_ADMIN"].includes(user?.role);

  const load = () => api.get("/grievances").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const raise_ = async (e) => {
    e.preventDefault();
    try { await api.post("/grievances", form); alert("Grievance raised"); setOpen(false); setForm({ category: "WORKPLACE", subject: "", description: "", confidential: false }); load(); }
    catch (err) { alert(apiError(err.response?.data?.detail)); }
  };
  const setStatus = async (g, status) => {
    try { await api.put(`/grievances/${g.id}`, { status, resolution: resolution || g.resolution }); setDetail(null); setResolution(""); load(); }
    catch (err) { alert(apiError(err.response?.data?.detail)); }
  };
  const addComment = async (g) => {
    if (!comment.trim()) return;
    const { data } = await api.post(`/grievances/${g.id}/comments`, { text: comment });
    setDetail(data); setComment(""); load();
  };
  if (!items) return <Loading />;

  return (
    <div>
      <PageHeader title="Grievances" subtitle="Raise and track workplace concerns — confidentially if needed">
        <button data-testid="raise-grievance-btn" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> Raise Grievance</button>
      </PageHeader>
      {items.length === 0 ? <EmptyState icon={ShieldAlert} title="No grievances" hint="Concerns you raise appear here." /> : (
        <Card className="overflow-hidden"><div className="overflow-x-auto">
          <table className="w-full" data-testid="grievances-table">
            <thead><tr><th className="th">#</th><th className="th">Subject</th><th className="th">Category</th><th className="th">Raised By</th><th className="th">Status</th><th className="th">Date</th><th className="th"></th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map((g) => (
                <tr key={g.id} data-testid={`grievance-${g.id}`}>
                  <td className="td font-mono text-xs">{g.number}</td>
                  <td className="td font-medium text-slate-800 dark:text-slate-100">{g.subject}{g.confidential && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-600 dark:bg-red-500/15">CONFIDENTIAL</span>}</td>
                  <td className="td text-slate-500">{g.category}</td>
                  <td className="td text-slate-500">{g.raisedByName || "—"}</td>
                  <td className="td"><Badge value={g.status} tone={STATUS_TONE[g.status]} /></td>
                  <td className="td text-slate-500">{fmtDate(g.createdAt)}</td>
                  <td className="td text-right"><button data-testid={`grievance-view-${g.id}`} onClick={() => { setDetail(g); setResolution(g.resolution || ""); }} className="btn-outline !px-3 !py-1 text-xs">Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Raise a Grievance"
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="grv-form" className="btn-accent">Submit</button></>}>
        <form id="grv-form" onSubmit={raise_} className="space-y-4">
          <div><label className="label">Category *</label>
            <select data-testid="grv-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input">{CATS.map((c) => <option key={c}>{c}</option>)}</select></div>
          <div><label className="label">Subject *</label><input required data-testid="grv-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="input" /></div>
          <div><label className="label">Description</label><textarea data-testid="grv-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input h-24 py-2" /></div>
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <input type="checkbox" data-testid="grv-confidential" checked={form.confidential} onChange={(e) => setForm({ ...form, confidential: e.target.checked })} /> Keep my identity confidential (visible only to HR)
          </label>
        </form>
      </Modal>

      {detail && (
        <Modal open onClose={() => setDetail(null)} title={`${detail.number} · ${detail.subject}`}>
          <div className="space-y-4" data-testid="grievance-detail">
            <div className="flex flex-wrap gap-2"><Badge value={detail.category} /><Badge value={detail.status} tone={STATUS_TONE[detail.status]} /></div>
            <p className="text-sm text-slate-600 dark:text-slate-300">{detail.description || "No description."}</p>
            {detail.resolution && <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"><b>Resolution:</b> {detail.resolution}</div>}
            <div>
              <p className="mb-1.5 flex items-center gap-1 text-xs font-semibold uppercase text-slate-400"><MessageSquare size={12} /> Comments</p>
              <div className="max-h-40 space-y-2 overflow-y-auto">
                {(detail.comments || []).map((c, i) => (
                  <div key={i} className="rounded-lg bg-slate-50 p-2 text-sm dark:bg-slate-800/50"><b className="text-xs text-slate-500">{c.by}</b><p>{c.text}</p></div>
                ))}
                {(detail.comments || []).length === 0 && <p className="text-xs text-slate-400">No comments yet.</p>}
              </div>
              <div className="mt-2 flex gap-2">
                <input data-testid="grv-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} className="input flex-1" placeholder="Add a comment…" />
                <button data-testid="grv-comment-btn" onClick={() => addComment(detail)} className="btn-outline">Post</button>
              </div>
            </div>
            {isHR && detail.status !== "CLOSED" && (
              <div className="space-y-2 border-t border-slate-200 pt-3 dark:border-slate-700">
                <label className="label">Resolution note</label>
                <textarea data-testid="grv-resolution" value={resolution} onChange={(e) => setResolution(e.target.value)} className="input h-16 py-2" />
                <div className="flex flex-wrap gap-2">
                  {detail.status === "OPEN" && <button data-testid="grv-review-btn" onClick={() => setStatus(detail, "IN_REVIEW")} className="btn-outline text-xs">Mark In Review</button>}
                  <button data-testid="grv-resolve-btn" onClick={() => setStatus(detail, "RESOLVED")} className="btn-accent text-xs">Resolve</button>
                  <button data-testid="grv-close-btn" onClick={() => setStatus(detail, "CLOSED")} className="btn-outline text-xs">Close</button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
